import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { EntitlementAuditEntry, EntitlementSource, EntitlementStatus, ModuleEntitlement, ProductMembership, Tenant, UserIdentity } from '../types';

export async function getTenants(): Promise<Tenant[]> {
  const snapshot = await getDocs(query(collection(db, 'tenants'), limit(100)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as Tenant).sort((a, b) => a.name.localeCompare(b.name));
}
export async function getTeam(): Promise<Array<ProductMembership & UserIdentity>> {
  const snapshot = await getDocs(query(collection(db, 'productMemberships'), where('status', '==', 'active'), limit(100)));
  return Promise.all(snapshot.docs.map(async item => {
    const member = { uid: item.id, ...item.data() } as ProductMembership;
    const identity = await getDoc(doc(db, 'users', item.id));
    return { ...member, ...(identity.exists() ? identity.data() as UserIdentity : {}) };
  }));
}
export async function getIdentity(uid: string | null): Promise<UserIdentity | null> {
  if (!uid) return null; const result = await getDoc(doc(db, 'users', uid));
  return result.exists() ? { uid, ...result.data() } as UserIdentity : null;
}

export async function getEntitlements(tenantId: string): Promise<ModuleEntitlement[]> {
  const snapshot = await getDocs(collection(db, 'tenants', tenantId, 'entitlements'));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as ModuleEntitlement);
}

export interface SetEntitlementInput {
  tenantId: string;
  applicationKey: string;
  moduleKey: string;
  status: EntitlementStatus;
  effectiveFrom?: string | null;
  effectiveUntil?: string | null;
  source: EntitlementSource;
  notes?: string | null;
}
// The only write path for any entitlement — a Cloud Function in the
// Compass repo (functions/src/entitlements.ts), never a direct Firestore
// write from here. Validates against the canonical registry and writes
// the paired audit entry server-side.
export async function setEntitlement(input: SetEntitlementInput): Promise<{ id: string }> {
  const call = httpsCallable<SetEntitlementInput, { id: string }>(functions, 'setEntitlement');
  const result = await call(input);
  return result.data;
}

export async function getEntitlementAuditLog(tenantId: string, max = 50): Promise<EntitlementAuditEntry[]> {
  const snapshot = await getDocs(
    query(collection(db, 'entitlementAuditLog'), where('tenantId', '==', tenantId), orderBy('changedAt', 'desc'), limit(max)),
  );
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as EntitlementAuditEntry);
}

export async function getAllEntitlementAuditLog(max = 100): Promise<EntitlementAuditEntry[]> {
  const snapshot = await getDocs(query(collection(db, 'entitlementAuditLog'), orderBy('changedAt', 'desc'), limit(max)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as EntitlementAuditEntry);
}
